import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getFileIcon } from '../../utils/fileIconUtils';
import { formatFileSize } from '../../services/closeoutDocsService';
import { formatDate } from '../../../../utils';

/**
 * UnsupportedViewer Component
 * Displays file information for documents that cannot be viewed inline
 * Shows file details and instructs user to go back and download from the list
 *
 * @param {Object} props
 * @param {string} props.filename - Name of the file
 * @param {string} props.extension - File extension
 * @param {Object} props.document - Full document object with metadata
 * @returns {React.ReactElement} Unsupported file viewer
 */
const UnsupportedViewer = ({ filename, extension, document }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: theme.palette.grey[50],
                p: { xs: 2, sm: 3 },
            }}
        >
            <Card
                elevation={3}
                sx={{
                    maxWidth: 650,
                    width: '100%',
                    textAlign: 'center',
                    borderRadius: 2,
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                    {/* File Icon */}
                    <Box sx={{ mb: 3 }}>{getFileIcon(extension, 'large')}</Box>

                    {/* Filename */}
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            mb: 1.5,
                            wordBreak: 'break-word',
                            color: theme.palette.text.primary,
                        }}
                    >
                        {filename}
                    </Typography>

                    {/* File Type Badge */}
                    {extension && (
                        <Chip
                            label={extension.toUpperCase()}
                            size="medium"
                            color="primary"
                            variant="outlined"
                            sx={{
                                mb: 2.5,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        />
                    )}

                    {/* Message */}
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            mb: 3,
                            lineHeight: 1.6,
                        }}
                    >
                        This file type cannot be previewed in the browser. Please go back and
                        download it from the document list to view it.
                    </Typography>

                    {/* File Metadata */}
                    {document && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                textAlign: 'left',
                                backgroundColor: theme.palette.grey[100],
                                p: 2.5,
                                borderRadius: 1.5,
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {document.fileSizeBytes && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        File Size:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatFileSize(document.fileSizeBytes)}
                                    </Typography>
                                </Box>
                            )}
                            {document.uploadedByName && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Uploaded By:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {document.uploadedByName}
                                    </Typography>
                                </Box>
                            )}
                            {document.uploadedAt && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Upload Date:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatDate(document.uploadedAt)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

UnsupportedViewer.propTypes = {
    filename: PropTypes.string.isRequired,
    extension: PropTypes.string,
    document: PropTypes.shape({
        originalFilename: PropTypes.string,
        fileExtension: PropTypes.string,
        fileSizeBytes: PropTypes.number,
        uploadedByName: PropTypes.string,
        uploadedAt: PropTypes.string,
    }),
};

UnsupportedViewer.defaultProps = {
    extension: '',
    document: null,
};

export default UnsupportedViewer;
